import { ReactNode } from "react";
import AssetHealthIcon from "../assets/icons/asset-health.svg?react";
import CopilotIcon from "../assets/icons/copilot.svg?react";
import DashboardsIcon from "../assets/icons/dashboards.svg?react";
import SupportIcon from "../assets/icons/headphones.svg?react";
import ServiceHistoryIcon from "../assets/icons/service-log.svg?react";
import TrendexIcon from "../assets/icons/trendex.svg?react";

import { buildConditionMonitoringUrl } from "../helper/conditionMonitoringUrlHelper";
import { withGlobalParams } from "../site-selection/withGlobalParams";
import {
  ASSET_HEALTH_BASE_URL,
  COPILOT_BASE_URL,
  SERVICE_LOG_BASE_URL,
  SUPPORT_LINE_BASE_URL,
  TRENDEX_BASE_URL,
} from "./urls";

export enum ModuleId {
  Dashboards = 1,
  Trendex = 2,
  Copilot = 3,
  AdminPanel = 4,
  AssetHealth = 5,
  Analytics = 6,
  ServiceLog = 7,
  SupportLine = 8,
  Mobile = 9,
  Any = 100,
}

export enum ModuleCategory {
  Base,
  Premium,
}

export type ModuleDto = {
  id: ModuleId;
  category: ModuleCategory;
  visible: boolean;
  enabled: boolean;
};

export type ModuleStaticDetails = {
  id: ModuleId;
  title: string;
  description: string[];
  icon: ReactNode;
  getHref: (
    site: string,
    rootEquipment?: string,
    systemOverviewReport?: string,
    projectOverviewReport?: string,
    productionArea?: string
  ) => string;
  isHighlighted?: boolean;
  supportsMultiSite: boolean;
};

export type Module = ModuleStaticDetails & ModuleDto;

export function getAllModuleStaticDetails(): ModuleStaticDetails[] {
  return [
    {
      id: ModuleId.Copilot,
      title: "Copilot",
      description: [
        `AI-powered support`,
        `Raise questions about your system`,
        `Fast access to your documents, manuals and reports`,
      ],
      icon: <CopilotIcon />,
      getHref: (site) =>
        withGlobalParams(`${COPILOT_BASE_URL}/site/${site}`, [site]),
      supportsMultiSite: false,
    },
    {
      id: ModuleId.Trendex,
      title: "Trendex",
      description: [
        `Diagnostic and Troubleshooting`,
        "Access to fault event files",
        "Alarms and events",
      ],
      icon: <TrendexIcon />,
      getHref: () => `${TRENDEX_BASE_URL}`,
      supportsMultiSite: true,
    },
    {
      id: ModuleId.ServiceLog,
      title: "Service Log",
      description: [
        `Overview of Service events`,
        "Access to service reports",
        "Service recommendations",
        "System lifecycle status overview",
      ],
      icon: <ServiceHistoryIcon />,
      getHref: () => `${SERVICE_LOG_BASE_URL}`,
      isHighlighted: false,
      supportsMultiSite: true,
    },
    {
      id: ModuleId.SupportLine,
      title: "Support",
      description: [
        "Open a Support case",
        "Contact the ABB 24/7/365 SupportLine",
        "Support log overview",
      ],
      icon: <SupportIcon />,
      getHref: () => `${SUPPORT_LINE_BASE_URL}`,
      isHighlighted: true,
      supportsMultiSite: true,
    },
    {
      id: ModuleId.Dashboards,
      title: "Condition Monitoring",
      description: [
        `Real-time system performance monitoring`,
        "Anomaly detection and signal forecasting",
      ],
      icon: <DashboardsIcon />,
      getHref: (
        site,
        rootEquipment,
        systemOverviewReport,
        projectOverviewReport,
        productionArea
      ) =>
        buildConditionMonitoringUrl({
          site,
          rootEquipment,
          productionArea,
          systemOverviewReport,
          projectOverviewReport,
        }),

      isHighlighted: false,
      supportsMultiSite: false,
    },
    {
      id: ModuleId.AssetHealth,
      title: "Asset Health Monitoring",
      description: [
        "Asset Health monitoring portal",
        "Asset Health reports",
        "System performance KPIs",
      ],
      icon: <AssetHealthIcon />,
      getHref: (site) => `${ASSET_HEALTH_BASE_URL}/site/${site}`,
      isHighlighted: false,
      supportsMultiSite: false,
    },
  ];
}
